import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Search, ChevronDown, X } from "lucide-react";

import { Pagination } from "../components/common/Pagination";
import { useGameRecords } from "../hooks/useGameRecords";
import { useGames } from "../hooks/useGames";
import { useGameTables } from "../hooks/useGameTables";
import { GameRecordTable } from "../components/gameRecord/GameRecordsTable";
import { DateRangeFilter } from "../components/customers/customerDetail/DateRangeFilter";

export const GameRecords: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedDateFrom, setSelectedDateFrom] = useState<string>("");
  const [selectedDateTo, setSelectedDateTo] = useState<string>("");

  const [selectedGames, setSelectedGames] = useState<string[]>([]);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);

  const [showGameDropdown, setShowGameDropdown] = useState(false);
  const [showTableDropdown, setShowTableDropdown] = useState(false);

  const itemsPerPage = 25;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedDate,
    selectedDateFrom,
    selectedDateTo,
    selectedGames,
    selectedTables,
  ]);

  const { data: gamesData } = useGames({ limit: 100 });
  const { data: tablesData } = useGameTables({ limit: 100 });

  const games = gamesData?.games || [];
  const tables = tablesData?.gameTables || [];

  const getDateFilters = useCallback(() => {
    if (selectedDate) {
      return {
        dateFrom: selectedDate,
        dateTo: selectedDate,
      };
    } else if (selectedDateFrom && selectedDateTo) {
      return {
        dateFrom: selectedDateFrom,
        dateTo: selectedDateTo,
      };
    }
    return {};
  }, [selectedDate, selectedDateFrom, selectedDateTo]);

  const queryParams = useMemo(
    () => ({
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      search: debouncedSearchTerm || undefined,
      ...getDateFilters(),
      ...(selectedGames.length > 0 && { gameIds: selectedGames }),
      ...(selectedTables.length > 0 && { gameTableIds: selectedTables }),
    }),
    [
      currentPage,
      debouncedSearchTerm,
      selectedGames,
      selectedTables,
      getDateFilters,
    ],
  );

  const {
    data: gameRecordsData,
    isLoading,
    isError,
    error,
  } = useGameRecords(queryParams);

  const gameRecords = gameRecordsData?.gameRecords || [];
  const total = gameRecordsData?.total || 0;
  const totalPages = Math.ceil(total / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setSelectedDateFrom("");
    setSelectedDateTo("");
  };

  const handleDateRangeChange = (dateFrom: string, dateTo: string) => {
    setSelectedDateFrom(dateFrom);
    setSelectedDateTo(dateTo);
    setSelectedDate("");
  };

  const handleDateClear = () => {
    setSelectedDate("");
    setSelectedDateFrom("");
    setSelectedDateTo("");
  };

  const handleGamesChange = (gameIds: string[]) => {
    setSelectedGames(gameIds);
  };

  const handleTablesChange = (tableIds: string[]) => {
    setSelectedTables(tableIds);
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedDate("");
    setSelectedDateFrom("");
    setSelectedDateTo("");
    setSelectedGames([]);
    setSelectedTables([]);
  };

  const filteredTables =
    selectedGames.length > 0
      ? tables.filter((table) => selectedGames.includes(table.gameId))
      : tables;

  const handleGameToggle = (gameId: string) => {
    const newSelectedGames = selectedGames.includes(gameId)
      ? selectedGames.filter((id) => id !== gameId)
      : [...selectedGames, gameId];
    handleGamesChange(newSelectedGames);
  };

  const handleTableToggle = (tableId: string) => {
    const newSelectedTables = selectedTables.includes(tableId)
      ? selectedTables.filter((id) => id !== tableId)
      : [...selectedTables, tableId];
    handleTablesChange(newSelectedTables);
  };

  const getSelectedGamesText = () => {
    if (selectedGames.length === 0) return "Filter by Game";
    if (selectedGames.length === 1) {
      const game = games.find((g) => g.id === selectedGames[0]);
      return game?.name || "1 game selected";
    }
    return `${selectedGames.length} games selected`;
  };

  const getSelectedTablesText = () => {
    if (selectedTables.length === 0) return "Filter by Table";
    if (selectedTables.length === 1) {
      const table = tables.find((t) => t.id === selectedTables[0]);
      return table?.name || "1 table selected";
    }
    return `${selectedTables.length} tables selected`;
  };

  const hasActiveFilters = Boolean(
    searchTerm ||
      selectedDate ||
      (selectedDateFrom && selectedDateTo) ||
      selectedGames.length > 0 ||
      selectedTables.length > 0,
  );

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-800/20 border border-red-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-400 mb-2">
              Error Loading Game Records
            </h3>
            <p className="text-red-300">
              {error?.message ||
                "Failed to load game records. Please try again."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Search and Filters */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <div className="flex flex-col gap-4">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search game records..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filter Controls */}
            <div className="flex flex-wrap gap-4 relative">
              {/* Date Range Filter */}
              <DateRangeFilter
                selectedDate={selectedDate}
                selectedDateFrom={selectedDateFrom}
                selectedDateTo={selectedDateTo}
                onDateChange={handleDateChange}
                onDateRangeChange={handleDateRangeChange}
                onClear={handleDateClear}
              />

              {/* Game Filter */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowGameDropdown(!showGameDropdown);
                    setShowTableDropdown(false);
                  }}
                  className="flex items-center bg-slate-700 rounded px-4 py-2 text-white border border-slate-600 hover:bg-slate-600 transition-colors min-w-[180px]"
                >
                  <span className="truncate flex-1 text-left">
                    {getSelectedGamesText()}
                  </span>
                  {selectedGames.length > 0 && (
                    <X
                      className="w-4 h-4 ml-2 text-slate-400 hover:text-white flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGamesChange([]);
                      }}
                    />
                  )}
                  <ChevronDown className="w-4 h-4 ml-2 text-slate-400 flex-shrink-0" />
                </button>

                {showGameDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-50 min-w-[250px] max-h-60 overflow-y-auto">
                    <div className="p-2 border-b border-slate-600">
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm font-medium">
                          Select Games
                        </span>
                        <button
                          onClick={() => handleGamesChange([])}
                          className="text-xs text-slate-400 hover:text-white"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                    {games.map((game) => (
                      <label
                        key={game.id}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-slate-600 cursor-pointer text-white text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={selectedGames.includes(game.id)}
                          onChange={() => handleGameToggle(game.id)}
                          className="w-4 h-4 text-blue-600 bg-slate-600 border-slate-500 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <span className="flex-1">{game.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Table Filter */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowTableDropdown(!showTableDropdown);
                    setShowGameDropdown(false);
                  }}
                  className="flex items-center bg-slate-700 rounded px-4 py-2 text-white border border-slate-600 hover:bg-slate-600 transition-colors min-w-[180px]"
                >
                  <span className="truncate flex-1 text-left">
                    {getSelectedTablesText()}
                  </span>
                  {selectedTables.length > 0 && (
                    <X
                      className="w-4 h-4 ml-2 text-slate-400 hover:text-white flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTablesChange([]);
                      }}
                    />
                  )}
                  <ChevronDown className="w-4 h-4 ml-2 text-slate-400 flex-shrink-0" />
                </button>

                {showTableDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-50 min-w-[250px] max-h-60 overflow-y-auto">
                    <div className="p-2 border-b border-slate-600">
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm font-medium">
                          Select Tables
                        </span>
                        <button
                          onClick={() => handleTablesChange([])}
                          className="text-xs text-slate-400 hover:text-white"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                    {filteredTables.map((table) => (
                      <label
                        key={table.id}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-slate-600 cursor-pointer text-white text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTables.includes(table.id)}
                          onChange={() => handleTableToggle(table.id)}
                          className="w-4 h-4 text-blue-600 bg-slate-600 border-slate-500 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <span className="flex-1">{table.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Clear All Filters Button */}
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors font-medium whitespace-nowrap"
                >
                  <X size={16} />
                  Clear All
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Game Records Table */}
        <div className="mb-6">
          <GameRecordTable
            gameRecords={gameRecords}
            isLoading={isLoading}
            showCustomerColumn={true}
            hasActiveFilters={hasActiveFilters}
            selectedDate={selectedDate}
            selectedDateFrom={selectedDateFrom}
            selectedDateTo={selectedDateTo}
            selectedGames={selectedGames}
            selectedTables={selectedTables}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
          />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Stats */}
        {gameRecords.length > 0 && (
          <div className="mt-6 text-sm text-slate-400 text-center">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, total)} of {total} game
            records
            {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
          </div>
        )}

        {/* Close dropdowns when clicking outside */}
        {(showGameDropdown || showTableDropdown) && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setShowGameDropdown(false);
              setShowTableDropdown(false);
            }}
          />
        )}
      </div>
    </div>
  );
};
